import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Buildings, ArrowsClockwise } from "@phosphor-icons/react";

export default function ClientesAdmin() {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [selections, setSelections] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clientes/grupos");
      setGrupos(data);
      const sel = {};
      data.forEach((g) => { sel[g.nome] = g.business_partner_principal; });
      setSelections(sel);
    } catch {
      toast.error("Erro ao carregar grupos de clientes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (nome) => {
    const bp = selections[nome];
    setSaving((s) => ({ ...s, [nome]: true }));
    try {
      await api.patch(`/clientes/grupos/${encodeURIComponent(nome)}`, {
        business_partner_principal: bp,
      });
      toast.success("BP principal atualizado");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving((s) => ({ ...s, [nome]: false }));
    }
  };

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-6 max-w-6xl">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-[11px] tracking-[0.25em] text-[#6b89a8] uppercase">Administrativo</p>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Buildings weight="duotone" className="w-8 h-8 text-[#40B6A2]" />
              Grupos de Clientes SAP
            </h1>
            <p className="text-sm text-[#6b89a8] mt-1">
              Clientes com múltiplos Business Partners — defina qual é o BP principal de cada grupo
            </p>
          </div>
          <Button
            onClick={load}
            variant="outline"
            className="border-[#123a5e] text-[#8aa9c9] hover:bg-[#0B2D4D]"
            data-testid="btn-refresh-grupos"
          >
            <ArrowsClockwise className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        <div className="border border-[#123a5e] rounded-sm overflow-hidden">
          <div className="p-4 border-b border-[#123a5e] bg-[#0B2D4D] flex items-center justify-between">
            <h2 className="text-sm tracking-[0.2em] uppercase text-[#8aa9c9]">
              Grupos com múltiplos BPs
            </h2>
            <span className="text-xs text-[#6b89a8] font-mono-data">
              {loading ? "—" : `${grupos.length} grupos`}
            </span>
          </div>

          {loading ? (
            <div className="p-10 text-center text-[#6b89a8] text-sm">Carregando...</div>
          ) : grupos.length === 0 ? (
            <div className="p-10 text-center text-[#6b89a8] text-sm">
              Nenhum grupo com múltiplos BPs encontrado.
              <br />
              <span className="text-xs">
                Execute a sincronização SAP para popular os dados.
              </span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#061729] border-b border-[#123a5e]">
                  <tr className="text-left text-[10px] tracking-[0.2em] uppercase text-[#6b89a8]">
                    <th className="p-3">Nome do Grupo</th>
                    <th className="p-3">CNPJ</th>
                    <th className="p-3 text-center">Qtd BPs</th>
                    <th className="p-3">Todos os BPs</th>
                    <th className="p-3">BP Principal</th>
                    <th className="p-3">Definido por</th>
                    <th className="p-3">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {grupos.map((g) => {
                    const changed = selections[g.nome] !== g.business_partner_principal;
                    return (
                      <tr
                        key={g.nome}
                        data-testid={`row-grupo-${g.nome}`}
                        className="border-b border-[#0e2e4e] hover:bg-[#0B2D4D]/50"
                      >
                        <td className="p-3 font-medium max-w-[220px]">
                          <span className="truncate block" title={g.nome}>{g.nome}</span>
                        </td>
                        <td className="p-3 text-xs font-mono-data text-[#b8cde2]">
                          {g.cnpj || "—"}
                        </td>
                        <td className="p-3 text-center">
                          <span className="text-xs font-mono-data bg-[#40B6A2]/10 text-[#40B6A2] px-2 py-0.5 rounded-sm">
                            {g.business_partners?.length ?? 1}
                          </span>
                        </td>
                        <td className="p-3 max-w-[200px]">
                          <span className="text-[11px] text-[#6b89a8] font-mono-data break-all">
                            {g.business_partners?.join(" · ")}
                          </span>
                        </td>
                        <td className="p-3">
                          <select
                            value={selections[g.nome] ?? ""}
                            onChange={(e) =>
                              setSelections((s) => ({ ...s, [g.nome]: e.target.value }))
                            }
                            data-testid={`select-bp-${g.nome}`}
                            className="text-xs px-2 py-1 border rounded-sm bg-[#061729] border-[#123a5e] font-mono-data min-w-[110px] text-white"
                          >
                            {(g.business_partners ?? []).map((bp) => (
                              <option key={bp} value={bp} className="bg-[#061729]">
                                {bp}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 text-xs text-[#6b89a8]">
                          {g.definido_por ?? "—"}
                        </td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            disabled={!changed || saving[g.nome]}
                            onClick={() => save(g.nome)}
                            data-testid={`btn-save-${g.nome}`}
                            className="bg-[#40B6A2] text-[#061729] hover:bg-[#359a89] disabled:opacity-30 text-xs h-7 px-3"
                          >
                            {saving[g.nome] ? "..." : "Salvar"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
